<template>
  <standard-dialog
    v-model="model"
    :title="$t('set_new_price')"
    icon="mdi-cash-edit"
    :max-width="480"
    :save-text="$t('set_price')"
    :disabled="saving"
    :loading="saving"
    @save="save"
  >
    <global-confirm-box ref="confirmBox" />

    <span class="caption font-weight-medium d-block mb-1">{{ $t("payment_method") }}</span>
    <v-radio-group v-model="form.paying_type" hide-details class="mt-0 mb-3">
      <v-radio
        v-for="option in payingTypes"
        :key="option.value"
        :label="option.text"
        :value="option.value"
      ></v-radio>
    </v-radio-group>

    <text-field
      v-model="form.amount"
      type="number"
      :label="$t('amount') + ' ' + amountUnit"
      required
      :rules="[rules.required]"
    />
    <span class="caption error--text d-block mb-2" v-if="errors.amount">{{ errors.amount }}</span>

    <date-picker v-model="form.valid_from" single-date :label="$t('price_valid_from')" :min="minValidFrom" />

    <v-alert v-if="currentPrice" type="info" text dense class="mt-3 mb-0">
      {{ $t("current_price_will_be_archived", { price: currentPrice.opis }) }}
    </v-alert>
  </standard-dialog>
</template>

<script>
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import hasModel from "@/mixins/hasModel";
import hasValidation from "@/mixins/hasValidation";
import hasSnackbar from "@/mixins/hasSnackbar";
import pricingService from "@/services/pricing";

const SANITY_LIMITS = {
  2: 40,
  1: 5000,
  3: 100,
};

export default {
  name: "SetPriceDialog",
  mixins: [hasModel, hasValidation, hasSnackbar],
  components: {
    StandardDialog,
  },
  props: {
    restaurantId: {
      type: [Number, String],
      required: true,
    },
    serviceId: {
      type: [Number, String],
      required: true,
    },
    payingTypes: {
      type: Array,
      default: () => [],
    },
    currentPrice: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      saving: false,
      errors: {},
      form: {
        paying_type: null,
        amount: "",
        valid_from: "",
      },
    };
  },
  watch: {
    model(val) {
      if (val) this.resetForm();
    },
  },
  computed: {
    amountUnit() {
      if (Number(this.form.paying_type) === 2) return "%";
      return this.getCurrencyByRestaurantId(this.restaurantId) || "KM";
    },
    minValidFrom() {
      return this.currentPrice?.valid_from || undefined;
    },
  },
  methods: {
    resetForm() {
      this.errors = {};
      this.form = {
        paying_type: this.payingTypes[0]?.value ?? null,
        amount: "",
        valid_from: "",
      };
    },
    close() {
      this.model = false;
    },
    validate() {
      const errors = {};
      const amount = Number(this.form.amount);
      if (!this.form.amount || isNaN(amount) || amount <= 0) {
        errors.amount = this.$t("validation.required");
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    async confirmSanityIfNeeded() {
      const amount = Number(this.form.amount);
      const limit = SANITY_LIMITS[Number(this.form.paying_type)];
      if (!limit || amount <= limit) return true;
      try {
        await this.$refs.confirmBox.open(
          this.$t("please_confirm"),
          this.$t("amount_looks_unusual"),
          { color: "warning" }
        );
        return true;
      } catch (e) {
        return false;
      }
    },
    async save() {
      if (!this.validate()) return;
      const proceed = await this.confirmSanityIfNeeded();
      if (!proceed) return;

      this.saving = true;
      try {
        const { data } = await pricingService.createRestaurantPrice(this.restaurantId, {
          service_id: this.serviceId,
          paying_type: this.form.paying_type,
          amount: Number(this.form.amount),
          valid_from: this.form.valid_from || null,
        });
        this.showSuccessSnackbar(this.$t("price_saved_successfully"));
        this.$emit("saved", data);
        this.model = false;
      } catch (error) {
        this.handleSaveError(error);
      } finally {
        this.saving = false;
      }
    },
    handleSaveError(error) {
      if (error?.response?.status === 422) {
        const serverErrors = error.response.data?.errors || {};
        const parsed = {};
        Object.keys(serverErrors).forEach((key) => {
          const field = key.split(".").pop();
          parsed[field] = serverErrors[key][0];
        });
        this.errors = { ...this.errors, ...parsed };
        this.showErrorSnackbar(error.response.data?.message || this.$t("please_fix_errors"));
      } else {
        this.showErrorSnackbar(this.$t("error_saving_price"));
      }
    },
  },
};
</script>
